<script setup lang="ts">
import { LayoutDashboard, Settings } from 'lucide-vue-next';
import { t } from '../i18n';
import type { Network } from '../types';
import BrandAvatar from './BrandAvatar.vue';

defineProps<{
    networks: Network[];
    /**
     * The picture and the name for the profile button, resolved by the caller.
     *
     * Not `profile.avatar_url` and not the two name fields: the dashboard header shows
     * the same person one screen away, and it resolves them differently — a Telegram
     * photo stands in when the account has no picture of its own. Reading the raw profile
     * here put a Telegram photo at the top and a generated gradient at the bottom of the
     * same screen, for the same face. One source, so they cannot drift again.
     */
    avatar: string | null;
    name: string;
    activeNetworkId: number | null;
    homeActive: boolean;
}>();
defineEmits<{
    home: [];
    select: [number];
    settings: [];
    profile: [];
}>();
</script>

<template>
    <aside class="rail">
        <button
            class="rail-home"
            :class="{ active: homeActive }"
            type="button"
            :aria-label="t('Головна')"
            :aria-current="homeActive ? 'page' : undefined"
            @click="$emit('home')"
        >
            <LayoutDashboard :size="21" :stroke-width="2.15" />
        </button>
        <div class="rail-divider" />
        <button
            v-for="network in networks"
            :key="network.id"
            class="workspace"
            :class="{ active: network.id === activeNetworkId }"
            type="button"
            :aria-label="network.name"
            :aria-current="network.id === activeNetworkId ? 'page' : undefined"
            @click="$emit('select', network.id)"
        >
            <BrandAvatar
                :src="network.logo_url || network.clubs[0]?.logo_url"
                :name="network.name"
            />
        </button>
        <span class="rail-spacer" />
        <button
            class="rail-settings"
            type="button"
            :aria-label="t('Налаштування')"
            @click="$emit('settings')"
        >
            <Settings :size="22" />
        </button>
        <button
            class="profile-button"
            type="button"
            :aria-label="t('Профіль')"
            @click="$emit('profile')"
        >
            <BrandAvatar :src="avatar" :name="name" />
        </button>
    </aside>
</template>

<style scoped>
:global(:root[data-theme='dark']) {
    --workspace-rail-bg: #060a12;
    --workspace-rail-border: #121b29;
}

.rail {
    position: fixed;
    z-index: 10;
    inset: 0 auto 0 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    width: var(--rail-width);
    padding: calc(10px + var(--app-safe-area-top)) 9px
        calc(10px + env(safe-area-inset-bottom));
    background: var(
        --workspace-rail-bg,
        color-mix(in srgb, var(--secondary) 90%, var(--bg))
    );
    border-right: 1px solid
        var(
            --workspace-rail-border,
            color-mix(in srgb, var(--hint) 10%, transparent)
        );
}
.rail button {
    border: 0;
    padding: 0;
    color: var(--text);
    cursor: pointer;
}
.rail-home {
    position: relative;
    display: grid;
    place-items: center;
    width: 50px;
    height: 50px;
    border: 1px solid transparent !important;
    border-radius: 14px;
    background:
        linear-gradient(var(--surface), var(--surface)) padding-box,
        linear-gradient(
                145deg,
                rgba(70, 174, 255, 0.5),
                rgba(123, 73, 255, 0.42)
            )
            border-box;
    color: var(--accent) !important;
    box-shadow:
        0 8px 18px rgba(20, 79, 160, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.08);
    isolation: isolate;
    overflow: hidden;
    transition:
        transform 0.18s ease,
        background 0.18s ease,
        color 0.18s ease,
        box-shadow 0.18s ease;
}
.rail-home::before {
    content: '';
    position: absolute;
    z-index: -1;
    top: -14px;
    right: -12px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    opacity: 0;
    transition: opacity 0.18s ease;
}
.rail-home.active {
    border-color: rgba(255, 255, 255, 0.2) !important;
    background: linear-gradient(140deg, #20b8ff 0%, #2878f0 48%, #7138e7 100%);
    color: white !important;
    box-shadow:
        0 10px 24px rgba(35, 125, 243, 0.34),
        0 4px 10px rgba(89, 56, 220, 0.18),
        inset 0 1px 0 rgba(255, 255, 255, 0.34);
}
.rail-home.active::before {
    opacity: 1;
}
.rail-settings {
    display: grid;
    place-items: center;
    width: 50px;
    height: 50px;
    border: 1px solid color-mix(in srgb, var(--accent) 18%, transparent) !important;
    border-radius: 14px;
    background: color-mix(in srgb, var(--accent) 9%, var(--surface)) !important;
    color: var(--accent) !important;
    transition:
        transform 0.18s ease,
        background 0.18s ease;
}
.rail-settings {
    color: var(--hint) !important;
    border-color: color-mix(in srgb, var(--hint) 14%, transparent) !important;
    background: var(--surface) !important;
}
.rail-settings:active,
.rail-home:active {
    transform: scale(0.94);
}
.rail-divider {
    width: 28px;
    height: 1px;
    background: color-mix(in srgb, var(--hint) 18%, transparent);
}
.workspace {
    position: relative;
    display: grid;
    place-items: center;
    width: 50px;
    height: 50px;
    background: transparent;
}
.workspace::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -9px;
    width: 3px;
    height: 0;
    transform: translateY(-50%);
    border-radius: 0 4px 4px 0;
    background: var(--accent);
    transition: height 0.2s;
}
.workspace.active::before {
    height: 28px;
}
.workspace :deep(.brand-avatar) {
    width: 50px;
    height: 50px;
    border-radius: 15px;
    transition:
        border-radius 0.2s,
        transform 0.2s;
}
.workspace.active :deep(.brand-avatar) {
    border-radius: 14px;
    transform: scale(1.04);
}
.rail-spacer {
    flex: 1;
}
.profile-button {
    display: grid;
    place-items: center;
    width: 50px;
    height: 50px;
    overflow: hidden;
    border-radius: 16px;
    background: var(--surface) !important;
    border: 2px solid color-mix(in srgb, var(--accent) 30%, transparent) !important;
}
.profile-button :deep(.brand-avatar) {
    width: 100%;
    height: 100%;
    border: 0;
    border-radius: 13px;
}
</style>
